import React, { useState, useEffect } from 'react';
import Card from '../components/common/Card';
import ActionMenu from '../components/common/ActionMenu';
import { CheckCircle2, Pencil, Power, Trash2 } from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import api from '../services/api';

const CourseLevels = () => {
  const [examBodies, setExamBodies] = useState([]);
  const [allLevels, setAllLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [formData, setFormData] = useState({
    exam_body_id: '',
    level_number: '',
    level_name: '',
    sort_order: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/course-levels');
      const data = response.data;

      // Set exam bodies from the response
      setExamBodies(data);

      // Flatten all levels into a single array for tabular display
      const flattenedLevels = [];
      data.forEach(examBody => {
        if (examBody.levels && examBody.levels.length > 0) {
          examBody.levels.forEach(level => {
            flattenedLevels.push({
              ...level,
              exam_body_id: examBody.exam_body_id,
              exam_body_name: examBody.exam_body_name,
              exam_body_code: examBody.exam_body_code
            });
          });
        }
      });
      setAllLevels(flattenedLevels);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.exam_body_id) {
      toast.error('Please select an exam body');
      return;
    }

    if (!formData.level_number && !formData.level_name) {
      toast.error('Either level number or level name is required');
      return;
    }

    setLoading(true);
    try {
      if (editingLevel) {
        await api.put(`/course-levels/${editingLevel.id}`, formData);
        toast.success('Level updated successfully');
      } else {
        await api.post('/course-levels', formData);
        toast.success('Level added successfully');
      }
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving level');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (level) => {
    setEditingLevel(level);
    setFormData({
      exam_body_id: level.exam_body_id,
      level_number: level.level_number || '',
      level_name: level.level_name || '',
      sort_order: level.sort_order || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (level) => {
    if (!window.confirm(`Delete "${getLevelDisplay(level)}" from ${level.exam_body_name}?`)) return;

    setLoading(true);
    try {
      await api.delete(`/course-levels/${level.id}`);
      toast.success('Level deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting level');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingLevel(null);
    setFormData({
      exam_body_id: '',
      level_number: '',
      level_name: '',
      sort_order: 0
    });
  };

  const getLevelDisplay = (level) => {
    if (level.level_number) {
      return `Level ${level.level_number}`;
    }
    return level.level_name || '—';
  };

  const getBadgeColor = (examBodyCode, level) => {
    if (examBodyCode === 'TVET-CDACC') {
      switch(level.level_number) {
        case 6: return 'bg-purple-100 text-purple-800';
        case 5: return 'bg-blue-100 text-blue-800';
        case 4: return 'bg-green-100 text-green-800';
        case 3: return 'bg-yellow-100 text-yellow-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    } else if (examBodyCode === 'KNEC') {
      switch(level.level_name) {
        case 'Diploma': return 'bg-indigo-100 text-indigo-800';
        case 'Certificate': return 'bg-cyan-100 text-cyan-800';
        case 'Artisan': return 'bg-orange-100 text-orange-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    } else if (examBodyCode === 'NITA') {
      switch(level.level_name) {
        case 'First Class': return 'bg-amber-100 text-amber-800';
        case 'Pass': return 'bg-lime-100 text-lime-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    }
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Course Levels Management</h1>
          <p className="text-gray-600 mt-1">
            Manage levels for each exam body
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          + Add Level
        </Button>
      </div>

      {/* Summary Cards - Displayed at the top */}
      {examBodies.length > 0 && (
        <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {examBodies.map(examBody => {
            const levelCount = allLevels.filter(l => l.exam_body_id === examBody.exam_body_id).length;
            return (
              <Card key={examBody.exam_body_id} className="text-center">
                <div className={`text-lg font-semibold mb-2 ${
                  examBody.exam_body_code === 'TVET-CDACC' ? 'text-blue-600' :
                  examBody.exam_body_code === 'KNEC' ? 'text-green-600' : 'text-yellow-600'
                }`}>
                  {examBody.exam_body_name}
                </div>
                <div className="text-3xl font-bold text-gray-800">{levelCount}</div>
                <div className="text-sm text-gray-500 mt-1">Levels Configured</div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Level Form */}
      {showForm && (
        <Card className="mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingLevel ? 'Edit Level' : 'Add New Level'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Exam Body *</label>
                <select
                  className="input-field"
                  value={formData.exam_body_id}
                  onChange={(e) => setFormData({...formData, exam_body_id: e.target.value})}
                  required
                >
                  <option value="">-- Select Exam Body --</option>
                  {examBodies.map(body => (
                    <option key={body.exam_body_id} value={body.exam_body_id}>
                      {body.exam_body_name}
                    </option>
                  ))}
                </select>
              </div>

              <Input
                label="Level Number (for TVET-CDACC)"
                type="number"
                min="1"
                max="10"
                value={formData.level_number}
                onChange={(e) => setFormData({...formData, level_number: e.target.value})}
                placeholder="e.g., 3, 4, 5, 6"
              />

              <Input
                label="Level Name (for KNEC/NITA)"
                value={formData.level_name}
                onChange={(e) => setFormData({...formData, level_name: e.target.value})}
                placeholder="e.g., Diploma, Certificate, Artisan, Grade I,II,III"
              />

              <Input
                label="Sort Order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({...formData, sort_order: e.target.value})}
                placeholder="Lower numbers appear first"
              />
            </div>
            <div className="flex gap-3 mt-4">
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (editingLevel ? 'Update Level' : 'Add Level')}
              </Button>
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Levels Table - Tabular List */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg overflow-hidden shadow">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Exam Body
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sort
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {allLevels.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  No levels found. Click "Add Level" to create one.
                </td>
              </tr>
            ) : (
              allLevels.map((level) => (
                <tr key={level.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-2 ${
                        level.exam_body_code === 'TVET-CDACC' ? 'bg-blue-500' :
                        level.exam_body_code === 'KNEC' ? 'bg-green-500' : 'bg-yellow-500'
                      }`}></div>
                      <span className="text-sm font-medium text-gray-900">
                        {level.exam_body_name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getBadgeColor(level.exam_body_code, level)}`}>
                      {getLevelDisplay(level)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {level.level_number ? `Level ${level.level_number}` : level.level_name}
                    </div>
                    {level.level_number && level.level_name && (
                      <div className="text-xs text-gray-500 mt-1">Name: {level.level_name}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{level.sort_order}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <ActionMenu
                      label={`Actions for ${level.exam_body_name || 'level'}`}
                      items={[
                        { label: 'Edit', icon: Pencil, onClick: () => handleEdit(level) },
                        {
                          label: 'Delete',
                          icon: Trash2,
                          variant: 'danger',
                          onClick: () => handleDelete(level),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CourseLevels;
